import { FormEvent, useState } from 'react';
import { X } from 'lucide-react';
import { ContentReport, OwnerReportAction, respondToContentReport } from '../../backend/moderationService';

interface ReportResponseModalProps {
  report: ContentReport;
  onClose: () => void;
  onSaved: () => void;
}

const actionLabels: Record<OwnerReportAction, string> = {
  will_correct: 'I will correct it',
  corrected: 'I have corrected it',
  keep_as_is: 'Keep as is'
};

export function ReportResponseModal({ report, onClose, onSaved }: ReportResponseModalProps) {
  const [action, setAction] = useState<OwnerReportAction>('will_correct');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanNote = note.trim();
    if (cleanNote.length < 10 || cleanNote.length > 1000) {
      setError('Please enter a reason between 10 and 1000 characters.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await respondToContentReport(report, action, cleanNote);
      onSaved();
      onClose();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Could not submit your response.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[180] flex items-center justify-center bg-slate-950/60 p-4">
      <form onSubmit={event => void submit(event)} className="w-full max-w-md space-y-4 rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-950">Respond to report</h3>
            <p className="mt-1 text-xs text-slate-500">{report.contentTitle || 'Reported content'}</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <label className="block text-xs font-semibold text-slate-700">
          Your response
          <select value={action} onChange={event => setAction(event.target.value as OwnerReportAction)} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm">
            {Object.entries(actionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold text-slate-700">
          Reason (required, 10-1000 characters)
          <textarea value={note} onChange={event => setNote(event.target.value)} minLength={10} maxLength={1000} required rows={5} className="mt-1.5 w-full resize-y rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-blue-700" />
        </label>
        {error && <p role="alert" className="text-xs text-rose-700">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-xl bg-slate-100 px-4 py-2.5 text-xs font-bold text-slate-700">Cancel</button>
          <button type="submit" disabled={saving || note.trim().length < 10} className="rounded-xl bg-blue-950 px-4 py-2.5 text-xs font-bold text-white disabled:opacity-50">{saving ? 'Saving…' : 'Submit response'}</button>
        </div>
      </form>
    </div>
  );
}
